import React, { createContext, useContext, useState, useEffect } from 'react';

const LanguageContext = createContext();

export function useLanguage() {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within LanguageProvider');
  }
  return context;
}

export function LanguageProvider({ children }) {
  const [currentLanguage, setCurrentLanguage] = useState(null);
  const [availableLanguages, setAvailableLanguages] = useState([]);
  const [loading, setLoading] = useState(true);

  // Load languages - using default languages for now
  // TODO: Implement VPS API endpoint for languages if needed
  useEffect(() => {
    loadLanguages();
  }, []);

  const loadLanguages = async () => {
    try {
      // Default language configuration
      const langs = [
        { id: 'en', name: 'English', code: 'en', isDefault: false },
        { id: 'zh-TW', name: '繁體中文', code: 'zh-TW', isDefault: true },
        { id: 'zh-CN', name: '简体中文', code: 'zh-CN', isDefault: false }
      ];

      const defaultLang = langs.find(l => l.isDefault) || langs[0];

      setAvailableLanguages(langs);

      // Check if user has selected a language in localStorage
      const savedLangId = localStorage.getItem('selectedLanguage');
      if (savedLangId) {
        const savedLang = langs.find(l => l.id === savedLangId);
        if (savedLang) {
          setCurrentLanguage(savedLang);
        } else {
          setCurrentLanguage(defaultLang);
        }
      } else {
        setCurrentLanguage(defaultLang || langs[0]);
      }

      setLoading(false);
    } catch (err) {
      console.error('Failed to load languages:', err);
      setLoading(false);
    }
  };

  // Switch language
  const switchLanguage = (langId) => {
    const lang = availableLanguages.find(l => l.id === langId);
    if (lang) {
      setCurrentLanguage(lang);
      localStorage.setItem('selectedLanguage', langId);
    }
  };

  // Get translation
  const t = (key, fallback = key) => {
    if (!currentLanguage || !currentLanguage.translations) {
      return fallback;
    }
    return currentLanguage.translations[key] || fallback;
  };

  const value = {
    currentLanguage,
    availableLanguages,
    switchLanguage,
    t,
    loading
  };

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
}
